<script setup>
import { onMounted, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';

import ContactsDetailsLayout from 'dashboard/components-next/Contacts/ContactsDetailsLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactIdentityCard from 'dashboard/components-next/Contacts/ContactIdentityCard.vue';
import ContactDetails from 'dashboard/components-next/Contacts/Pages/ContactDetails.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactMedia from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMedia.vue';
import ContactMerge from 'dashboard/components-next/Contacts/ContactsSidebar/ContactMerge.vue';
import ContactCustomAttributes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributes.vue';
import ContactAppointmentHistory from 'dashboard/components-next/appointments/ContactAppointmentHistory.vue';
import ContactPipelineHistory from 'dashboard/components-next/pipeline/ContactPipelineHistory.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const store = useStore();
const route = useRoute();
const router = useRouter();

const contact = useMapGetter('contacts/getContactById');
const uiFlags = useMapGetter('contacts/getUIFlags');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const contactAttributeDefinitions = useMapGetter(
  'attributes/getContactAttributes'
);

const activeSection = ref('detalhes');
const contactMergeRef = ref(null);

const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
const isMergingContact = computed(() => uiFlags.value.isMerging);
const isUpdatingContact = computed(() => uiFlags.value.isUpdating);

const selectedContact = computed(() => contact.value(route.params.contactId));
const currentAccountId = computed(() => store.getters.getCurrentAccountId);

const isAppointmentsEnabled = computed(() =>
  isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.APPOINTMENTS_MODULE
  )
);

const isPipelineEnabled = computed(() =>
  isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.PIPELINE_BOARD
  )
);

const showSpinner = computed(
  () => isFetchingItem.value || isMergingContact.value
);

const { t } = useI18n();

// Attributes used in this contact — mirrors ContactCustomAttributes' own
// "used attributes" logic, kept lightweight since it only needs a count.
const attributesCount = computed(() => {
  const definitions = contactAttributeDefinitions.value || [];
  const customAttributes = selectedContact.value?.customAttributes || {};
  return definitions.filter(
    ({ attributeKey }) => attributeKey in customAttributes
  ).length;
});

const BASE_SECTIONS = [
  {
    value: 'detalhes',
    key: 'DETAILS',
    icon: 'i-lucide-user-round',
    showHead: true,
  },
  {
    value: 'atributos',
    key: 'ATTRIBUTES',
    icon: 'i-lucide-list-checks',
    showHead: true,
  },
  {
    value: 'historico',
    key: 'HISTORY',
    icon: 'i-lucide-history',
    showHead: true,
  },
  {
    value: 'notas',
    key: 'NOTES',
    icon: 'i-lucide-sticky-note',
    showHead: true,
  },
  { value: 'midia', key: 'MEDIA', icon: 'i-lucide-image', showHead: true },
];

const navSections = computed(() => {
  const sections = [...BASE_SECTIONS];
  if (isPipelineEnabled.value) {
    sections.push({
      value: 'pipeline',
      key: 'PIPELINE',
      icon: 'i-lucide-kanban',
      showHead: false,
    });
  }
  if (isAppointmentsEnabled.value) {
    sections.push({
      value: 'atendimentos',
      key: 'APPOINTMENTS',
      icon: 'i-lucide-calendar-check',
      showHead: false,
    });
  }
  sections.push({
    value: 'mesclar',
    key: 'MERGE',
    icon: 'i-lucide-merge',
    showHead: false,
  });

  return sections.map(section => ({
    ...section,
    count: section.value === 'atributos' ? attributesCount.value : undefined,
  }));
});

const activeSectionMeta = computed(
  () =>
    navSections.value.find(section => section.value === activeSection.value) ||
    navSections.value[0]
);

const goToContactsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/contacts?page=1`);
  }
};

const fetchActiveContact = async () => {
  if (route.params.contactId) {
    await store.dispatch('contacts/show', { id: route.params.contactId });
    await store.dispatch(
      'contacts/fetchContactableInbox',
      route.params.contactId
    );
  }
};

const setActiveSection = value => {
  activeSection.value = value;
};

const fetchContactNotes = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactNotes/get', { contactId });
};

const fetchContactConversations = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactConversations/get', contactId);
};

const fetchAttributes = () => {
  store.dispatch('attributes/get');
};

const toggleContactBlock = async isBlocked => {
  const ALERT_MESSAGES = {
    success: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE'),
    },
    error: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE'),
    },
  };

  try {
    await store.dispatch(`contacts/update`, {
      ...selectedContact.value,
      blocked: !isBlocked,
    });
    useAlert(
      isBlocked ? ALERT_MESSAGES.success.unblock : ALERT_MESSAGES.success.block
    );
  } catch (error) {
    useAlert(
      isBlocked ? ALERT_MESSAGES.error.unblock : ALERT_MESSAGES.error.block
    );
  }
};

onMounted(() => {
  fetchActiveContact();
  fetchContactNotes();
  fetchContactConversations();
  fetchAttributes();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-surface-1"
  >
    <ContactsDetailsLayout
      :button-label="$t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
      :selected-contact="selectedContact"
      is-detail-view
      :show-pagination-footer="false"
      :is-updating="isUpdatingContact"
      @go-to-contacts-list="goToContactsList"
      @toggle-block="toggleContactBlock"
    >
      <div
        v-if="showSpinner"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <div
        v-else-if="selectedContact"
        class="grid items-start grid-cols-1 gap-6 md:grid-cols-[18rem_1fr]"
      >
        <!-- Left rail: identity + section navigation -->
        <aside class="flex flex-col gap-4 md:sticky md:top-24">
          <ContactIdentityCard :selected-contact="selectedContact" />
          <nav
            class="flex flex-col gap-1 p-2 bg-n-solid-2 outline outline-1 outline-n-container -outline-offset-1 rounded-xl"
          >
            <button
              v-for="section in navSections"
              :key="section.value"
              type="button"
              class="relative flex items-center w-full gap-2.5 px-2.5 py-2 text-sm font-medium text-left transition-colors border-l-2 rounded-r-lg"
              :class="
                activeSection === section.value
                  ? 'bg-n-blue-3 text-n-blue-11 border-n-blue-9'
                  : 'border-transparent text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
              "
              @click="setActiveSection(section.value)"
            >
              <span :class="section.icon" class="flex-shrink-0 size-4" />
              <span class="truncate">
                {{ t(`CONTACTS_LAYOUT.SIDEBAR.TABS.${section.key}`) }}
              </span>
              <span
                v-if="section.count !== undefined"
                class="ml-auto text-[11px] font-semibold rounded-full px-1.5 py-0.5 min-w-5 text-center"
                :class="
                  activeSection === section.value
                    ? 'bg-n-blue-4 text-n-blue-11'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                {{ section.count }}
              </span>
            </button>
          </nav>
        </aside>

        <!-- Active section content -->
        <main class="min-w-0">
          <div
            class="bg-n-solid-2 outline outline-1 outline-n-container -outline-offset-1 rounded-xl"
          >
            <div v-if="activeSectionMeta.showHead" class="px-6 pt-6 pb-4">
              <h2 class="text-base font-medium text-n-slate-12">
                {{ t(`CONTACTS_LAYOUT.SIDEBAR.TABS.${activeSectionMeta.key}`) }}
              </h2>
              <p class="mt-0.5 text-xs text-n-slate-11">
                {{
                  t(
                    `CONTACTS_LAYOUT.SIDEBAR.PANEL_SUBTITLES.${activeSectionMeta.key}`
                  )
                }}
              </p>
            </div>
            <div :class="activeSectionMeta.showHead ? 'pb-6' : 'py-6'">
              <ContactDetails
                v-if="activeSection === 'detalhes'"
                class="px-6"
                :selected-contact="selectedContact"
                @go-to-contacts-list="goToContactsList"
              />
              <ContactCustomAttributes
                v-else-if="activeSection === 'atributos'"
                :selected-contact="selectedContact"
              />
              <ContactHistory v-else-if="activeSection === 'historico'" />
              <ContactNotes v-else-if="activeSection === 'notas'" />
              <ContactMedia v-else-if="activeSection === 'midia'" />
              <ContactPipelineHistory
                v-else-if="activeSection === 'pipeline' && selectedContact.id"
                :contact-id="selectedContact.id"
              />
              <ContactAppointmentHistory
                v-else-if="
                  activeSection === 'atendimentos' && selectedContact.id
                "
                :contact-id="selectedContact.id"
              />
              <ContactMerge
                v-else-if="activeSection === 'mesclar'"
                ref="contactMergeRef"
                :selected-contact="selectedContact"
                @go-to-contacts-list="goToContactsList"
                @reset-tab="setActiveSection('detalhes')"
              />
            </div>
          </div>
        </main>
      </div>
    </ContactsDetailsLayout>
  </div>
</template>
